import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchNews, updateNews, deleteNews } from '@/utils/graphqlClient';

export interface NewsAsset {
  id: string;
  collection_id: string;
  type: string;
  page_id: string;
  slug: string;
  version: string;
  title: string;
  description?: string;
  primary_image?: string;
  secondary_image?: string;
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_markup_url?: string;
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_markup_url?: string;
  crop_aspect_ratio?: string;
  crop_coordinates?: string;
  header?: string;
  subheader?: string;
  comparison_mode?: string;
  secondary_version?: string;
  secondary_page_id?: string;
  primary_annotations?: string;
  secondary_annotations?: string;
  primary_viewport_state?: any;
  secondary_viewport_state?: any;
  created_at: string;
  updated_at: string;
  company_slug?: string;
  logo_url?: string;
  page?: {
    id: string;
    pricing_page_url?: string;
  };
}

export interface NewsItem {
  id: string;
  slug: string;
  page_id: string;
  version: string;
  before_version?: string;
  quarter?: string;
  hook?: string;
  description?: string;
  before_image_url?: string;
  after_image_url?: string;
  gif_url?: string;
  published: boolean;
  verified?: boolean;
  created_at?: string;
  zoom_level?: number;
  annotations?: any;
  asset_id?: string;
  price_change?: boolean;
  plan_change?: boolean;
  metric_change?: boolean;
  discount_change?: boolean;
  features_change?: boolean;
  assets?: NewsAsset[];
}

export interface NewsConnection {
  news: NewsItem[];
  totalCount: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  currentPage: number;
  pageSize: number;
  publishedCount: number;
  verifiedCount: number;
}

export interface UseNewsOptions {
  quarter?: string;
  slug?: string;
  published?: boolean | null;
  verified?: boolean | null;
  priceChange?: boolean | null;
  planChange?: boolean | null;
  metricChange?: boolean | null;
  discountChange?: boolean | null;
  featuresChange?: boolean | null;
  limit?: number;
  offset?: number;
}

/**
 * Hook for fetching news items with pagination and filters
 */
export function useNews(options: UseNewsOptions = {}) {
  const { quarter, slug, published, verified, priceChange, planChange, metricChange, discountChange, featuresChange, limit = 50, offset = 0 } = options;

  return useQuery({
    queryKey: ['news', { quarter, slug, published, verified, priceChange, planChange, metricChange, discountChange, featuresChange, limit, offset }],
    queryFn: async (): Promise<NewsConnection> => {
      const response = await fetchNews(quarter, slug, published, verified, priceChange, planChange, metricChange, discountChange, featuresChange, limit, offset);
      return response.news;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook for news-related mutations
 */
export function useNewsMutations() {
  const queryClient = useQueryClient();

  const updateNewsMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: any }) =>
      updateNews(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries(['news']);
    },
  });

  const deleteNewsMutation = useMutation({
    mutationFn: (id: string) => deleteNews(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['news']);
    },
  });

  return {
    updateNews: (variables: { id: string; input: any }, options?: any) =>
      updateNewsMutation.mutate(variables, options),
    deleteNews: (id: string, options?: any) =>
      deleteNewsMutation.mutate(id, options),
    isLoading: updateNewsMutation.isLoading || deleteNewsMutation.isLoading,
  };
}
